import to from 'await-to-js';
import { Entry, Node as ManagementNode } from 'contensis-management-api';
import deepCleaner from 'deep-cleaner';
import pl from 'pluralize';

import ContentAssetsMigrationService from './ContentAssetsMigrationService';
import {
  IContensisContext,
  MigrateModelsEntityResult,
  MigrateNode,
} from '~/models';
import { EntriesResult, MigrateRequest } from '~/models/Operations';
import ContensisRepository from '~/repositories/ContensisRepository';
import { EntryBuilder } from '~/transformations/EntryBuilder';
import {
  logMigrateResultsTable,
  logEntriesPreviewTable,
  logEntitiesTable,
  logNodesPreviewTable,
  logTagsPreviewTable,
} from '~/transformations/logging';
import { mapMigrateResult } from '~/transformations/mapEntriesMigrateResult';
import { mapSourceNodeToTargetNode } from '~/transformations/mapSourceNodeToTargetNode';
import ContentTypeHelper from '~/util/content-type/ContentTypeHelper';
import {
  completeProgressBar,
  logError,
  Logger,
  newProgressBar,
} from '~/util/logger';
import { merge, combineMerge } from '~/util/merge';
import { hasKeys } from '~/util/typeguards';
import { waitByResponseTime } from '~/util/wait';

class EntriesMigrationService extends ContentAssetsMigrationService {
  /**
   * Keep a note of the nodes we have migrated in this job separate from the entries migration count
   */
  nodesMigration: {
    created: string[];
    updated: string[];
    deleted: string[];
  };
  isCopyOperation: boolean;

  constructor(payload: MigrateRequest, preview: boolean, download = false) {
    super(payload, preview, download);
    this.nodesMigration = {
      created: [],
      updated: [],
      deleted: [],
    };
    this.isCopyOperation = hasKeys(payload.copyField || payload.updateField);
  }

  /**
   * Gets a list of entries from the Managemet API with the supplied query
   * @returns list of Entry, if requested with dependents these are included in the list of items
   */
  GetEntries = async ({ withDependents } = { withDependents: true }) => {
    if (withDependents) {
      // Loads content types and components into memory
      await this.HydrateContensisRepositories();

      // Loads all entries (with nodes and tags) from source and target repos into memory
      await this.GetEntriesToMigrate(false);
    }
    // Just gets entries from source repo
    else await this.source.entries.GetEntries();

    return this.source.entries.raw;
  };

  /**
   * Using SyncEntries means we migrate the entries to the targetProjects and then
    delete the entries from any other mentioned in syncProjects that are not in
    targetRepos so we can keep a number of projects in sync with one call
    that has differentiating targetProjects fed from webhook data
   * @returns EntriesResult
   */
  SyncEntries = async () => {
    const results = await this.MigrateEntries();
    let deleteResults = {} as EntriesResult;

    const cleanupRepos = Object.fromEntries(
      Object.entries(this.sync).filter(([project]) => !this.targets[project])
    );
    if (Object.keys(cleanupRepos))
      deleteResults = await this.DeleteEntries(false, cleanupRepos, true);

    return deepCleaner(
      merge(results, deleteResults, { arrayMerge: combineMerge })
    );
  };

  MigrateEntries = async () => {
    const { download, isPreview } = this;

    // Loads content types and components into memory
    this.payload.includeDefaults = false;
    if (!this.isHydrated) await this.HydrateContensisRepositories();

    // Loads all entries from source and target repos into memory
    await this.GetEntriesToMigrate();

    // Build and store the target entries
    // against each targetRepo.migrateEntries
    await this.BuildTagsToMigrate();
    await this.BuildTargetEntries();
    await this.BuildNodesToMigrate();

    const previewResult = mapMigrateResult(this, this.targets); // mapMigrateResult can be slow to complete with payloads > 10k
    if (Logger.shouldLog('warning')) {
      logTagsPreviewTable(previewResult, Object.keys(this.targets));
      logEntriesPreviewTable(previewResult, Object.keys(this.targets));
      logNodesPreviewTable(previewResult, Object.keys(this.targets));
    }

    if ((download || !isPreview) && this.errorCount && !this.ignoreErrors) {
      Logger.error(
        `[entries] Not committing ${this.opName} due to ${this.errorCount} errors`
      );
    } else if (download || !isPreview) {
      if (this.ignoreErrors)
        Logger.warning(
          `[entries] Committing ${this.opName} with ${this.errorCount} known errors`
        );
      if (download) await this.DownloadAndQueueContentAssets(isPreview);

      if (!isPreview) {
        await this.CommitEntries();
      }
    }

    let result = previewResult;
    if (!isPreview) {
      // mapMigrateResult can be slow to complete with payloads > 10k
      result = mapMigrateResult(this, this.targets);
    }

    logMigrateResultsTable(result, 'tags');
    logMigrateResultsTable(result);
    logMigrateResultsTable(result, 'nodes');

    Logger.success(
      `Entries ${this.opName} ${isPreview ? `preview ready` : 'complete'}`,
      true
    );

    return result;
  };

  /** Loads all entries and dependencies (including nodes and tags)
   *  from source and target repos into memory */
  GetEntriesToMigrate = async (logEntries = Logger.shouldLog('info')) => {
    const { source, targets, isCopyOperation } = this;

    const hasEntriesInPayload =
      isCopyOperation || source.entries.raw?.length > 0;

    // Entries are not supplied in the payload
    if (!hasEntriesInPayload) {
      // Get entries from source repo
      await source.entries.GetEntriesIncludingDependents();
    }
    // else get dependencies from source entries supplied in the payload
    else if (source.entries.raw.length > 0) {
      if (!source.repo) {
        // we have no content types when source is unknown
        // create a new context using contentTypes and components from the first target
        const targetContext = Object.values(targets)[0].context;
        source.context = new ContentTypeHelper(
          targetContext.contentTypes,
          targetContext.components,
          targetContext.tagGroups
        ) as IContensisContext;
        source.context.payload = this.payload;
      }
      await source.entries.CheckDependentEntriesAndNodes(source.entries.raw, {
        level: 1,
        getEntries: false,
        getNodes: false,
      });
    }

    if (logEntries)
      logEntitiesTable({
        entries: source.entries.raw,
        projectId: source.projectId,
      });

    if (source.entries.raw.length > 0)
      for (const [, target] of Object.entries(targets)) {
        // Fetch entries from target repo based on transformed entry ids
        // or any found asset uri
        const assetUris = !isCopyOperation
          ? Array.from(source.entries.dependencies.assetUris)
          : undefined;
        const entryIds = source.entries.entryIds.map((sourceId) =>
          target.transformGuid(sourceId || '')
        );

        void this.queue.add(() =>
          target.entries.GetEntriesIncludingDependents(
            {
              assetUris,
              entryIds,
            },
            isCopyOperation ? false : hasEntriesInPayload
          )
        );

        // Fetch any nodes from target repo based on transformed node ids or path
        const nodeIdsAndPaths = source.nodes.raw.map<[string, string]>((n) => [
          target.transformGuid(n.id || '', n.path),
          n.path,
        ]);
        for (const [id, path] of nodeIdsAndPaths)
          void this.queue.add(async () => {
            // try get node by path first
            const [err] = await target.nodes.GetNodes({ path }, 0);
            if (err)
              // then try getting node by id
              void this.queue.add(async () => {
                const [err] = await target.nodes.GetNodes({ id }, 0);
                // if (err) this.AddError(id, err);
              });
          });

        if (nodeIdsAndPaths.length > 0)
          void this.queue.add(async () => {
            await target.nodes.EnsureRootAncestor();
          });
      }

    // Wait until we've got all repo entries (and nodes)
    await this.queue.onIdle();

    // Now we have fetched all entries, check dependencies for any tags
    // except in copy operations, tags will already be attached to the entry
    if (!isCopyOperation) {
      const dependentTagIds = Object.entries(
        source.entries.dependencies.dependencyIds
      )
        .filter(([, d]) => d.some(({ type }) => type === 'tag'))
        .map(([tagId]) => tagId);

      await this.GetTagsToMigrate({
        ids: dependentTagIds,
      });
    }
  };

  BuildNodesToMigrate = async () => {
    for (const [, target] of Object.entries(this.targets)) {
      await target.nodes.BuildNodesToMigrate({
        onError: (n) => {
          this.AddError(n.originalId, n.error);
        },
      });
    }
  };

  CommitEntries = async () => {
    for (const [, target] of Object.entries(this.targets)) {
      await this.QueueTags(target);
      await this.QueueEntries(target, true);
      await this.CommitNodes(target);
      await this.QueueEntries(target, false);
    }
  };

  QueueEntries = async (target: ContensisRepository, firstPass = true) => {
    const createEntries: EntryBuilder[] = [];
    const twopassEntries: EntryBuilder[] = [];
    const updateEntries: EntryBuilder[] = [];
    const publishEntries: EntryBuilder[] = [];

    for (const me of (target.entries.migrate || []).filter(
      (me) => !me.isAsset()
    )) {
      if (firstPass) {
        switch (me.status) {
          case 'create':
            createEntries.push(me);
            break;
          case 'two-pass':
            twopassEntries.push(me);
            break;
          case 'update':
            updateEntries.push(me);
            break;
          case 'publish':
            publishEntries.push(me);
            break;
          default:
          // do nothing as we're not processing these statuses
        }
      } else if (me.status === 'two-pass') twopassEntries.push(me);
    }

    const newEntries = [...createEntries, ...twopassEntries];

    const totalEntries =
      newEntries.length + updateEntries.length + publishEntries.length;

    if (totalEntries)
      Logger.info(
        `[entries] Committing ${totalEntries} ${
          twopassEntries.length ? `${firstPass ? 'first' : 'second'} pass ` : ''
        }${pl('entry', totalEntries)} in ${Logger.highlightText(
          target.alias
        )} ${Logger.standardText(target.project.id)}`,
        true
      );

    const pr = newProgressBar(
      ` ${firstPass ? 'Loading' : 'Second pass'} [${
        target.project.id
      }] :bar :percent ${Logger.infoText(
        ':current/:total :elapseds/:etas :ratep/s'
      )}`,
      {
        output: this.payload.outputProgress,
        complete: Logger.successText('='),
        head: Logger.successText('>'),
        incomplete: ' ',
        width: 30,
        total: totalEntries,
      }
    );

    let currentIndex = 0;

    // Look for all entries to create where the entryTitle
    // is not unique and create them one-by-one to avoid any
    // potential slug is not unique errors when creating them in parallel
    const queuedCreateAndTwoPassEntries = [] as EntryBuilder[];
    const noConcurrencyCreateEntries = [] as EntryBuilder[];
    const noConcurrencyIds = new Set<string>();
    for (const me of newEntries) {
      if (!firstPass) {
        queuedCreateAndTwoPassEntries.push(me);
        continue;
      }

      if (noConcurrencyIds.has(me.id)) continue;

      const sharedEntryTitles = newEntries.filter(
        (ne) =>
          typeof ne.entryTitle === 'string' &&
          typeof me.entryTitle === 'string' &&
          ne.entryTitle.trim().toLowerCase() ===
            me.entryTitle.trim().toLowerCase()
      );
      if (sharedEntryTitles.length >= 1) {
        // keep track of all ids that share a title to avoid loading them twice
        for (const titleShare of sharedEntryTitles)
          noConcurrencyIds.add(titleShare.id);
        // we are safe to add the last entry in this list to the concurrent queue to load as normal
        const lastEntry = sharedEntryTitles.pop();
        if (lastEntry) queuedCreateAndTwoPassEntries.push(lastEntry);
        // add the remaining first n. entries with shared titles to the no concurrency list
        if (sharedEntryTitles.length > 0)
          noConcurrencyCreateEntries.push(...sharedEntryTitles);
      }
      // queue up new entry with distinct title
      else queuedCreateAndTwoPassEntries.push(me);
    }

    if (noConcurrencyCreateEntries?.length)
      Logger.info(
        `[entries] Creating ${noConcurrencyCreateEntries.length} first, ignoring concurrency to avoid "slug is not unique" errors in '${this.source.project.id}'`
      );

    for (const migrateEntry of noConcurrencyCreateEntries) {
      Logger.info(
        `[entries] ${++currentIndex}/${totalEntries} [${
          firstPass ? 'create' : 'update'
        }${migrateEntry.status === 'two-pass' ? ' stub' : ''}:${
          migrateEntry.contentTypeId
        }] '${migrateEntry.entryTitle}' | ${migrateEntry.id}`
      );
      await this.CommitEntry(migrateEntry, firstPass);
      pr?.tick();
    }

    // Load create and two-pass together, a two-pass entry on its firstPass should
    // create an unpublished stub entry
    for (const migrateEntry of queuedCreateAndTwoPassEntries) {
      // use queue so we can load entries in parallel
      void this.queue.add(async () => {
        Logger.info(
          `[entries] ${++currentIndex}/${totalEntries} [${
            firstPass ? 'create' : 'update'
          }${migrateEntry.status === 'two-pass' ? ' stub' : ''}:${
            migrateEntry.contentTypeId
          }] '${migrateEntry.entryTitle}' | ${migrateEntry.id}`
        );
        await this.CommitEntry(migrateEntry, firstPass);
        pr?.tick();
      });
    }
    await this.queue.onIdle();

    // Load update after the creates so any potential dependencies have
    // already been created first above
    for (const migrateEntry of updateEntries) {
      // use queue so we can load entries in parallel
      void this.queue.add(async () => {
        Logger.info(
          `[entries] ${++currentIndex}/${totalEntries} [update:${
            migrateEntry.contentTypeId
          }] '${migrateEntry.entryTitle}' | ${migrateEntry.id}`
        );
        await this.CommitEntry(migrateEntry, firstPass);
        pr?.tick();
      });
    }
    await this.queue.onIdle();

    // Publish entries marked with publish status as they require only publishing
    for (const migrateEntry of publishEntries) {
      // use queue so we can load entries in parallel
      void this.queue.add(async () => {
        Logger.info(
          `[entries] ${++currentIndex}/${totalEntries} [publish:${
            migrateEntry.contentTypeId
          }] '${migrateEntry.entryTitle}' | ${migrateEntry.id}`
        );
        if (migrateEntry.existing) {
          const [err, published] = await to(
            target.repo.PublishEntry(migrateEntry.existing)
          );
          if (err) {
            const mappedErr = this.AddError(migrateEntry.id, err);
            migrateEntry.error = mappedErr;
            migrateEntry.result = 'errors';
            logError(
              mappedErr.data || mappedErr,
              `${mappedErr.message} | ${migrateEntry.id} | ${migrateEntry.contentTypeId}`
            );
          }
          if (published) this.published.push(migrateEntry.id);
          pr?.tick();
        }
      });
    }
    await this.queue.onIdle();

    completeProgressBar();
  };

  CommitEntry = async (
    migrateEntry: EntryBuilder,
    firstPass: boolean,
    fromModel = false
  ): Promise<[AppError | null, Entry | undefined]> => {
    if (!migrateEntry.error) {
      const sourceEntry = migrateEntry.sourceEntry;

      const isFinal =
        !firstPass ||
        (firstPass &&
          !fromModel &&
          ['create', 'update', 'publish'].includes(migrateEntry.status));

      // Take the "final" entry to upsert on first pass for entries marked with
      // 'create' or 'update' status because we won't be sending these for a second pass
      const upsertEntry = migrateEntry.toJSON({ firstPass: !isFinal });

      // The entry can be published on first pass if the status is 'create' or 'update
      // Always publish on second pass
      const canPublish = isFinal;

      // Only publish if the source entry shows that it is published
      // Always publish an entry if it is created from a model
      const publishEntry =
        fromModel || (canPublish && migrateEntry.isPublished) || false;

      // Upsert entry
      const [error, newEntry] = await migrateEntry.contensis.repo.UpsertEntry(
        upsertEntry,
        migrateEntry.existing,
        publishEntry && !this.payload.noPublish
      );

      // in the context of a second pass, the "existing" entry becomes the one we sent
      // previously in the first pass
      if (fromModel || (firstPass && migrateEntry.status === 'two-pass'))
        migrateEntry.existing = newEntry || upsertEntry;

      if (!error && newEntry) {
        if (firstPass && ['create', 'two-pass'].includes(migrateEntry.status)) {
          migrateEntry.existing = newEntry;
          migrateEntry.result = 'created';
          this.created.push(sourceEntry?.sys.id || '');
        } else if (migrateEntry.status === 'update') {
          migrateEntry.result = 'updated';
          this.updated.push(sourceEntry?.sys.id || '');
        }
      }

      if (error) {
        const mappedError = this.AddError(sourceEntry?.sys.id || '', error);
        // Set error so two-pass entries do not attempt a 2nd pass
        migrateEntry.error = mappedError;
        migrateEntry.result = 'errors';
        logError(
          mappedError.data || error,
          `${mappedError.message} | ${upsertEntry.sys.id} | ${upsertEntry.sys.contentTypeId}`
        );
      }
      return [error, newEntry];
    }
    return [migrateEntry.error, undefined];
  };

  CommitNodes = async (target: ContensisRepository) => {
    const migrateNodes = target.nodes.migrateNodes.filter((n) =>
      ['create', 'update'].includes(n.status)
    );
    const totalNodes = migrateNodes.length;
    if (totalNodes)
      Logger.info(
        `[nodes] Committing ${totalNodes} site view nodes in ${Logger.highlightText(
          target.alias
        )} ${Logger.standardText(target.project.id)}'`,
        true
      );
    const pr = newProgressBar(
      ` Nodes [${target.project.id}] :bar :percent ${Logger.infoText(
        ':current/:total :elapseds/:etas :ratep/s'
      )}`,
      {
        output: this.payload.outputProgress,
        complete: Logger.successText('='),
        head: Logger.successText('>'),
        incomplete: ' ',
        width: 30,
        total: totalNodes,
      }
    );

    for (const migrateNode of migrateNodes) {
      await this.CommitNode(target, migrateNode);
      pr?.tick();
    }
    completeProgressBar();
  };

  CommitNode = async (
    target: ContensisRepository,
    migrateNode: MigrateNode
  ): Promise<keyof MigrateModelsEntityResult | undefined> => {
    const { existing, node } = migrateNode;
    let created: ManagementNode | undefined = undefined;
    let error: AppError | null = null;

    // Handle Management api error:
    // `When updating a node's parentId, it must be the only property changed.`
    if (
      node.parentId &&
      existing?.parentId &&
      existing.parentId !== node.parentId
    ) {
      // Map the existing node to Management format and replace the necessary ids
      const parentIdUpdateNode = {
        ...mapSourceNodeToTargetNode(target, existing),
        id: node.id,
        parentId: node.parentId,
        entryId: (existing as any).entryId,
      };

      // Make the update to just the parentId first
      [error, created] = await target.repo.UpdateOrCreateNode(
        existing as any,
        parentIdUpdateNode
      );
    }
    if (!error)
      // Handle all other updates
      [error, created] = await target.repo.UpdateOrCreateNode(
        existing as any,
        node as ManagementNode
      );
    if (error) {
      migrateNode.error = this.AddError(node.id, error);
      return 'errors';
    } else if (created && existing) {
      this.nodesMigration.updated.push(created.id);
      return 'updated';
    } else if (created) {
      this.nodesMigration.created.push(created.id);
      return 'created';
    }
  };

  DeleteEntries = async (
    recycle = false,
    targets = this.targets,
    isSync = false
  ) => {
    const { isPreview } = this;

    // Loads all entries from source and target repos into memory
    await this.GetEntriesToDelete(recycle, targets);

    if (!isPreview) {
      for (const [projectId, target] of Object.entries(targets)) {
        Logger.info(
          `${
            !recycle ? `Deleting` : `Recycling`
          } entries in project '${projectId}'`,
          true
        );

        for (const [i, entry] of Object.entries(
          (target.entries.migrate
            ?.filter((me) => me.status === 'delete' && !!me.existing)
            .map((me) => me.existing) || []) as Entry[]
        )) {
          void this.queue.add(async () => {
            const entryId = entry.sys.id || '';
            Logger.warning(
              `[delete] ${Number(i) + 1}/${
                target.entries.raw.length
              } ${projectId} [${entry.sys.contentTypeId}] ${
                entry.entryTitle
              } ${entryId}`
            );
            try {
              // Time the DeleteEntry operation to implement API rate limiting
              const isLastEntry = Number(i) === target.entries.raw.length - 1;
              await waitByResponseTime(async () => {
                await target.repo.DeleteEntry(entryId, recycle);
                this.deleted.push(entryId);
              }, isLastEntry);
            } catch (error: any) {
              Logger.error(
                `Error attempting to ${
                  !recycle ? `delete` : `recycle`
                } entry ${entryId}. Error was ${JSON.stringify(error)}`
              );
              this.AddError(entryId, error);
            }
          });
        }
      }
    }

    await this.queue.onIdle();

    Logger.info(
      `${!recycle ? `Delete` : `Recycle`} entries ${
        isPreview ? 'preview ready' : 'completed'
      }`,
      !isSync
    );
    return mapMigrateResult(this, targets);
  };

  GetEntriesToDelete = async (recycle = false, targets = this.targets) => {
    const { source } = this;
    const originalVersionStatus = source.query?.versionStatus;

    // If a source is supplied we will read entries from here and pass the
    // transformed ids to the GetEntries call when fetching entries to delete
    // from the target repos
    if (source.repo?.ProjectId() && Object.keys(targets).length) {
      if (source.query) {
        // Ensure we always select latest content when deleting
        if (!source.query.versionStatus) source.query.versionStatus = 'latest';
        if (!recycle && typeof source.query.includeDeleted === 'undefined')
          // Include recycled entries in the query if we are looking to delete permanently
          source.query.includeDeleted = true;
      }

      // Get entries from source repo (if we haven't got them in a previous operation)
      if (source.entries.raw.length > 0) {
        // TODO: stash original entries list in migrate without dependencies so we can reuse it here

        // Dump existing source entries as they contain dependencies which we DON'T want to delete
        source.entryIds = [];
        source.entries.raw = [];
      }
      await source.entries.GetEntries();
    }

    const promises = [] as Promise<any>[];

    for (const [, target] of Object.entries(targets)) {
      // Ensure we always select latest content when deleting
      if (target.query) {
        if (!target.query.versionStatus) target.query.versionStatus = 'latest';
        if (!recycle && typeof target.query.includeDeleted === 'undefined')
          // Include recycled entries in the query if we are looking to delete permanently
          target.query.includeDeleted = true;
      }
      // Fetch entries from target repo based on transformed entry ids
      // If there isn't any source entries to transform GetEntries will
      // fetch based on the query set in the target repo
      const entryIds = Object.fromEntries(
        source.entries.raw.map((e) => [
          target.models.isSourceRepo
            ? target.transformGuid(e.sys.id || '')
            : e.sys.id,
          e.sys.id,
        ])
      );

      promises.push(
        (async () => {
          const deleteEntries = await target.entries.GetEntries(
            Object.keys(entryIds)
          );

          deleteEntries.map((e) => {
            const entryBuilder = new EntryBuilder(target, source, e);
            entryBuilder.id = entryBuilder.originalId;
            entryBuilder.existing = e;
            entryBuilder.status = 'delete';
            target.entries.migrate.push(entryBuilder);
          });
        })()
      );
    }

    // Get all repo entries at the same time
    await Promise.all(promises);

    // Reset the original versionStatus as this affects the tests by
    // altering the query in the requests from source causing it to run
    // the test with a different payload
    if (source.query) source.query.versionStatus = originalVersionStatus;
  };
}

export default EntriesMigrationService;
